const WIDGET_CONFIG_CACHE_PROPERTY = '__ifoodWidgetTenantConfigCache';
export const IFOOD_WIDGET_PROFILE_TENANT_STORAGE_KEY = 'brutos-ifood-widget-tenant-v1';
export const IFOOD_WIDGET_PROFILE_LOCK_NAME = 'brutos-ifood-widget-profile-v1';

export const normalizeWidgetTenantKey = (value) => String(value || '').trim();

export const getBoundWidgetTenant = (windowRef) => {
  try {
    return normalizeWidgetTenantKey(
      windowRef?.localStorage?.getItem(IFOOD_WIDGET_PROFILE_TENANT_STORAGE_KEY)
    );
  } catch {
    return '';
  }
};

export const isWidgetProfileCompatibleWithTenant = (windowRef, tenantKey) => {
  const normalizedTenantKey = normalizeWidgetTenantKey(tenantKey);
  if (!normalizedTenantKey) return false;

  const boundTenantKey = getBoundWidgetTenant(windowRef);
  return !boundTenantKey || boundTenantKey === normalizedTenantKey;
};

export const bindWidgetProfileToTenant = (windowRef, tenantKey) => {
  const normalizedTenantKey = normalizeWidgetTenantKey(tenantKey);
  if (!normalizedTenantKey || !isWidgetProfileCompatibleWithTenant(windowRef, normalizedTenantKey)) {
    return false;
  }

  try {
    windowRef.localStorage.setItem(
      IFOOD_WIDGET_PROFILE_TENANT_STORAGE_KEY,
      normalizedTenantKey
    );
    return getBoundWidgetTenant(windowRef) === normalizedTenantKey;
  } catch {
    return false;
  }
};

export const shouldInvalidateWidgetForProfileStorageChange = (tenantKey, nextValue) => {
  const normalizedTenantKey = normalizeWidgetTenantKey(tenantKey);
  const nextTenantKey = normalizeWidgetTenantKey(nextValue);
  return Boolean(normalizedTenantKey) && nextTenantKey !== normalizedTenantKey;
};

export const runWithWidgetProfileLock = async (windowRef, callback) => {
  if (typeof windowRef?.navigator?.locks?.request !== 'function') {
    throw new Error('O navegador não oferece o bloqueio seguro exigido pelo widget iFood.');
  }

  return windowRef.navigator.locks.request(
    IFOOD_WIDGET_PROFILE_LOCK_NAME,
    { mode: 'exclusive' },
    callback
  );
};

export const buildIfoodWidgetSignature = ({
  tenantKey,
  widgetId,
  merchantIds = [],
}) => JSON.stringify([
  normalizeWidgetTenantKey(tenantKey),
  String(widgetId || '').trim(),
  merchantIds.map((merchantId) => String(merchantId || '').trim().toLowerCase()).filter(Boolean),
]);

export const loadTenantScopedWidgetConfig = (windowRef, tenantKey, loader) => {
  const normalizedTenantKey = normalizeWidgetTenantKey(tenantKey);
  if (!normalizedTenantKey) {
    return Promise.reject(new Error('Tenant ausente para carregar o widget iFood.'));
  }

  const cached = windowRef?.[WIDGET_CONFIG_CACHE_PROPERTY];
  if (cached?.tenantKey === normalizedTenantKey && cached.promise) {
    return cached.promise;
  }

  const entry = {
    tenantKey: normalizedTenantKey,
    promise: Promise.resolve().then(loader),
  };
  windowRef[WIDGET_CONFIG_CACHE_PROPERTY] = entry;

  entry.promise = entry.promise.catch((error) => {
    if (windowRef[WIDGET_CONFIG_CACHE_PROPERTY] === entry) {
      windowRef[WIDGET_CONFIG_CACHE_PROPERTY] = null;
    }
    throw error;
  });

  return entry.promise;
};

export const resetTenantScopedWidgetConfig = (windowRef, tenantKey) => {
  const cached = windowRef?.[WIDGET_CONFIG_CACHE_PROPERTY];
  if (!cached) return;

  const normalizedTenantKey = normalizeWidgetTenantKey(tenantKey);
  if (!normalizedTenantKey || cached.tenantKey === normalizedTenantKey) {
    windowRef[WIDGET_CONFIG_CACHE_PROPERTY] = null;
  }
};

export const isWidgetRuntimeCompatibleWithTenant = (windowRef, tenantKey) => {
  const normalizedTenantKey = normalizeWidgetTenantKey(tenantKey);
  if (!normalizedTenantKey) return false;
  if (!windowRef?.__ifoodWidgetInitialized) return true;
  return normalizeWidgetTenantKey(windowRef.__ifoodWidgetTenantKey) === normalizedTenantKey;
};

export const isWidgetRequestCurrentForTenant = (windowRef, tenantKey) => {
  const normalizedTenantKey = normalizeWidgetTenantKey(tenantKey);
  if (
    !normalizedTenantKey
    || windowRef?.__ifoodWidgetSessionChanging
    || windowRef?.__brutosExternalAuthChanged
  ) {
    return false;
  }
  return normalizeWidgetTenantKey(windowRef?.__ifoodWidgetRequestedTenantKey) === normalizedTenantKey;
};
